import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import {
	Form,
	FormGroup,
	Row,
	Col,
	Input,
	FormFeedback,
	Label,
	Button,
} from "reactstrap";
import Select from "react-select";
import userService from "services/UserService";
import actionObjectiveActionPlansService from "services/ActionObjectivesActionPlansService";
import { FloppyDisk, SignOut } from "@phosphor-icons/react";

const ModalCreateEditAction = ({
	show,
	onCloseClick,
	data,
	objective_id,
	objective_deadline,
	setData,
	getObjetivos,
	allowedResponsibleUserIds,
}) => {
	const [formData, setFormData] = useState({
		action: "",
		cost: "",
		deadline: "",
		active: 1,
		status: "",
		obs: "",
		objectives_action_plans_id: objective_id || "",
		responsible_id: "",
	});
	const [formErrors, setFormErrors] = useState({});
	const [usuarios, setUsuarios] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const fetchUsuarios = async () => {
		try {
			const response = await userService.get();
			let funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			// Restringe o select de responsável aos usuários que podem ver o plano
			if (allowedResponsibleUserIds && allowedResponsibleUserIds.length > 0) {
				const idSet = new Set(allowedResponsibleUserIds.map(String));
				funcoes = funcoes.filter((opt) => idSet.has(String(opt.value)));
			}
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (show) {
			fetchUsuarios();

			// Inicializa o formData quando o modal é aberto
			if (data && data.id) {
				console.log("new data", data);
				setFormData({
					action: data.action || "",
					cost: data.cost || "",
					deadline: data.deadline || "",
					active: data.active || 1,
					status: data.status || "",
					obs: data.obs || "",
					objectives_action_plans_id:
						data.objectives_action_plans_id || objective_id,
					responsible_id: data.responsible?.id || "",
				});
			} else {
				// Reseta o formData para uma nova ação
				setFormData({
					action: "",
					cost: "",
					deadline: "",
					active: 1,
					status: "",
					obs: "",
					objectives_action_plans_id: objective_id || "",
					responsible_id: "",
				});
			}

			// Limpa os erros anteriores
			setFormErrors({});
		} else {
			// Opcionalmente, reseta o formData quando o modal é fechado
			setFormData({
				action: "",
				cost: "",
				deadline: "",
				active: 1,
				status: "",
				obs: "",
				objectives_action_plans_id: objective_id || "",
				responsible_id: "",
			});
			setFormErrors({});
		}
	}, [show, allowedResponsibleUserIds]);

	const statusOptions = [
		{ value: "ANDAMENTO", label: "Em Andamento" },
		{ value: "STANDBY", label: "Standby" },
		{ value: "OK", label: "Ok" },
		{ value: "CANCELADO", label: "Cancelado" },
	];

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSelectChange = (selectedOption, fieldName) => {
		const selectedValue = selectedOption ? selectedOption.value : "";
		setFormData({ ...formData, [fieldName]: selectedValue });
	};

	const validate = () => {
		const errors = {};
		if (!formData.action) {
			errors.action = "Por favor, informe a ação!";
		}
		if (!formData.cost) {
			errors.cost = "Por favor, informe o custo!";
		}
		if (!formData.deadline) {
			errors.deadline = "Por favor, informe o prazo!";
		} else if (objective_deadline && formData.deadline > objective_deadline) {
			errors.deadline = "O prazo da ação não pode ser maior que o prazo do objetivo!";
		}
		if (!formData.status) {
			errors.status = "Por favor, selecione o status!";
		}
		if (!formData.responsible_id) {
			errors.responsible_id = "Por favor, informe o responsável!";
		}
		return errors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
		} else {
			setIsLoading(true);
			try {
				const val = { ...formData, cost: String(formData.cost) };
				let response;
				if (data && data.id) {
					response = await actionObjectiveActionPlansService.put(
						data.id,
						val,
					);
				} else {
					response = await actionObjectiveActionPlansService.post(
						val,
					);
				}
				setData(response);
				getObjetivos();
				onCloseClick();
			} catch (error) {
				console.log(error);
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<Modal
			size="sm"
			isOpen={show}
			toggle={onCloseClick}
			centered={true}
			style={{ minWidth: 800 }}
		>
			<div className="modal-content">
				<ModalBody className="px-4 py-3">
					<Col md="12">
						<Form onSubmit={handleSubmit}>
							<FormGroup className="mb-3">
								<Label htmlFor="action">
									Ação <span style={{ color: "red" }}>*</span>
								</Label>
								<Input
									name="action"
									type="textarea"
									placeholder="Insira a ação."
									onChange={handleInputChange}
									value={formData.action}
									invalid={!!formErrors.action}
								/>
								{formErrors.action && (
									<FormFeedback>
										{formErrors.action}
									</FormFeedback>
								)}
							</FormGroup>
							<Row>
								<Col md="3">
									<FormGroup className="mb-3">
										<Label htmlFor="responsible_id">
											Responsável{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Select
											styles={customStyles}
											placeholder="Selecione..."
											onChange={(selectedOption) =>
												handleSelectChange(
													selectedOption,
													"responsible_id",
												)
											}
											value={usuarios.find(
												(option) =>
													option.value ===
													formData.responsible_id,
											)}
											options={usuarios}
										/>
										{formErrors.responsible_id && (
											<div
												style={{
													color: "red",
													marginTop: 5,
												}}
											>
												{formErrors.responsible_id}
											</div>
										)}
									</FormGroup>
								</Col>
								<Col md="3">
									<FormGroup className="mb-3">
										<Label htmlFor="status">
											Status{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Select
											styles={customStyles}
											placeholder="Selecione..."
											onChange={(selectedOption) =>
												handleSelectChange(
													selectedOption,
													"status",
												)
											}
											value={statusOptions.find(
												(option) =>
													option.value ===
													formData.status,
											)}
											options={statusOptions}
										/>
										{formErrors.status && (
											<div
												style={{
													color: "red",
													marginTop: 5,
												}}
											>
												{formErrors.status}
											</div>
										)}
									</FormGroup>
								</Col>
								<Col md="3">
									<FormGroup className="mb-3">
										<Label htmlFor="deadline">
											Prazo{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Input
											name="deadline"
											type="date"
											placeholder="00/00/0000"
											className="form-control"
											onChange={handleInputChange}
											value={formData.deadline}
											invalid={!!formErrors.deadline}
										/>
										{formErrors.deadline && (
											<FormFeedback>
												{formErrors.deadline}
											</FormFeedback>
										)}
									</FormGroup>
								</Col>

								<Col md="3">
									<FormGroup className="mb-3">
										<Label htmlFor="cost">
											Custo{" "}
											<span style={{ color: "red" }}>
												*
											</span>
										</Label>
										<Input
											name="cost"
											type="number"
											placeholder="Insira o custo."
											onChange={handleInputChange}
											value={formData.cost}
											invalid={!!formErrors.cost}
										/>
										{formErrors.cost && (
											<FormFeedback>
												{formErrors.cost}
											</FormFeedback>
										)}
									</FormGroup>
								</Col>
								<Col md="12">
									<FormGroup className="mb-3">
										<Label htmlFor="obs">
											Observação
										</Label>
										<Input
											name="obs"
											type="textarea"
											placeholder="Insira a observação."
											onChange={handleInputChange}
											value={formData.obs}
											invalid={!!formErrors.obs}
										/>
										{formErrors.obs && (
											<FormFeedback>
												{formErrors.obs}
											</FormFeedback>
										)}
									</FormGroup>
								</Col>
							</Row>

							<Row className="py-1">
								<div className="hstack gap-2 justify-content-center mb-0">
									<Button
										style={{
											background: "none",
											cursor: "pointer",
											color: "black",
											marginLeft: 10,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
										}}
										type="submit"
										disabled={isLoading}
									>
										{isLoading ? (
											<span
												className="spinner-border spinner-border-sm mr-1"
												style={{ marginRight: 10 }}
											></span>
										) : null}
										<FloppyDisk size={16} /> Salvar
									</Button>
									<Button
										type="button"
										style={{
											background: "none",
											cursor: "pointer",
											color: "black",
											marginLeft: 10,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
										}}
										onClick={onCloseClick}
									>
										<SignOut size={16} /> Fechar
									</Button>
								</div>
							</Row>
						</Form>
					</Col>
				</ModalBody>
			</div>
		</Modal>
	);
};

ModalCreateEditAction.propTypes = {
	show: PropTypes.bool,
	onCloseClick: PropTypes.func,
	data: PropTypes.any,
	objective_id: PropTypes.any,
	objective_deadline: PropTypes.string,
	setData: PropTypes.func,
	getObjetivos: PropTypes.func,
	allowedResponsibleUserIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default ModalCreateEditAction;
